import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { buildProviderSlug } from "@/lib/provider-slug";
import { ProviderPhoto } from "@/components/ProviderPhoto";
import { Button } from "@/components/ui/button";
import {
  CheckCircle2,
  ShieldCheck,
  MapPin,
  Phone,
  Star,
  Languages as LanguagesIcon,
  CalendarCheck,
} from "lucide-react";
import { findInsurance, type Provider } from "@/data/heallexa";

const DAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function nextAvailableLabel(availableDays: string[]): string {
  if (!availableDays || availableDays.length === 0) return "Availability on request";
  const set = new Set(availableDays.map((d) => d.toLowerCase().slice(0, 3)));
  const today = new Date();
  for (let i = 0; i < 7; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    const key = DAY_KEYS[d.getDay()];
    if (set.has(key)) {
      if (i === 0) return "Next available: Today";
      if (i === 1) return "Next available: Tomorrow";
      return `Next available: ${DAY_LABELS[d.getDay()]}`;
    }
  }
  return "Availability on request";
}

function NextAvailable({ days }: { days: string[] }) {
  const [label, setLabel] = useState<string>("Check availability");
  useEffect(() => { setLabel(nextAvailableLabel(days)); }, [days]);
  return <>{label}</>;
}

export function ProviderCard({ p, highlights = [] }: { p: Provider; highlights?: string[] }) {
  const claimed = p.listing_status !== "unclaimed";
  const identityVerified = !!(p as any).verified_badge;
  const premium = p.listing_status === "premium";
  const reviewsCount = Number((p as any).reviews_count ?? 0);
  const hasRealRating = reviewsCount > 0 && Number(p.rating) > 0;
  return (
    <article className="relative border border-border bg-card p-4 hover:border-primary/40 hover:shadow-sm transition-all group">
      <Link
        to="/providers/$id"
        params={{ id: buildProviderSlug(p) }}
        aria-label={`View ${p.name} profile`}
        className="absolute inset-0 z-0"
      />
      <div className="relative z-10 flex items-start gap-4 pointer-events-none [&_a]:pointer-events-auto [&_button]:pointer-events-auto">
        <ProviderPhoto provider={p} className="flex-shrink-0 h-16 w-16 border border-border" />

        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-base font-semibold text-foreground leading-tight">{p.name}</h3>
            {identityVerified ? (
              <span className="inline-flex items-center gap-1 text-[10px] text-emerald-700 dark:text-emerald-400 font-medium border border-emerald-500/50 bg-emerald-50 dark:bg-emerald-950/30 px-1.5 py-0.5">
                <ShieldCheck className="h-3 w-3" /> Identity Verified
              </span>
            ) : claimed && (
              <span className="inline-flex items-center gap-1 text-[10px] text-primary font-medium border border-primary/30 px-1.5 py-0.5">
                <CheckCircle2 className="h-3 w-3" /> Verified
              </span>
            )}
            {premium && (
              <span className="text-[10px] bg-foreground text-background px-1.5 py-0.5 font-medium">Premium</span>
            )}
            {(() => { const npi = p.npi?.trim(); return npi && /^\d{10}$/.test(npi) ? (
              <a
                href={`https://npiregistry.cms.hhs.gov/provider-view/${npi}`}
                target="_blank"
                rel="noopener noreferrer nofollow"
                onClick={(e) => e.stopPropagation()}
                title="View NPI record on the NPPES registry"
                className="inline-flex items-center gap-1 text-[10px] font-mono text-muted-foreground border border-border px-1.5 py-0.5 hover:border-primary/40 hover:text-primary transition-colors"
              >
                NPI&nbsp;·&nbsp;{npi}
              </a>
            ) : null; })()}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5 truncate">{p.practice} · {p.specialty}</p>

          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5 truncate">
              <MapPin className="h-3 w-3 shrink-0" />
              <span className="truncate">{[p.address, p.city, p.state].filter(Boolean).join(", ")}{p.zip ? ` ${p.zip}` : ""}</span>
            </span>
            {p.phone && (
              <a href={`tel:${p.phone}`} onClick={(e) => e.stopPropagation()} className="flex items-center gap-1.5 text-primary hover:underline truncate">
                <Phone className="h-3 w-3 shrink-0" />{p.phone}
              </a>
            )}
            <span className="flex items-center gap-1.5" suppressHydrationWarning>
              <CalendarCheck className="h-3 w-3 shrink-0" />
              <NextAvailable days={p.available_days ?? []} />
            </span>
            {hasRealRating ? (
              <span className="flex items-center gap-1.5">
                <Star className="h-3 w-3 fill-current text-primary shrink-0" />
                {Number(p.rating).toFixed(1)} ({reviewsCount})
              </span>
            ) : (
              <span className="flex items-center gap-1.5 text-muted-foreground/70">
                <Star className="h-3 w-3 shrink-0" /> No reviews yet
              </span>
            )}
            {(p.languages?.length ?? 0) > 0 && (
              <span className="flex items-center gap-1.5 sm:col-span-2 truncate">
                <LanguagesIcon className="h-3 w-3 shrink-0" />
                <span className="truncate">{(p.languages ?? []).slice(0, 4).join(", ")}{(p.languages?.length ?? 0) > 4 ? "…" : ""}</span>
              </span>
            )}
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-1">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground/70 mr-1">Insurance:</span>
            {p.insurance.length === 0 ? (
              <span className="text-[11px] text-muted-foreground">Not specified</span>
            ) : (
              <>
                {p.insurance.slice(0, 5).map((id) => {
                  const ins = findInsurance(id);
                  if (!ins) return null;
                  return (
                    <span key={id} className="text-[10px] px-1.5 py-0.5 border" style={{ borderColor: ins.color, color: ins.color }}>
                      {ins.short}
                    </span>
                  );
                })}
                {p.insurance.length > 5 && (
                  <span className="text-[10px] px-1.5 py-0.5 text-muted-foreground">+{p.insurance.length - 5}</span>
                )}
              </>
            )}
          </div>

          {highlights.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {highlights.slice(0, 4).map((h) => (
                <span key={h} className="text-[10px] px-1.5 py-0.5 bg-primary/10 text-primary font-medium">
                  {h}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="hidden md:flex flex-col gap-1.5 items-end shrink-0">
          <Button asChild size="sm" className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-none h-8 text-xs">
            <Link to="/providers/$id" params={{ id: buildProviderSlug(p) }}>Book online</Link>
          </Button>
          {!claimed && (
            <Button variant="outline" size="sm" asChild className="h-8 text-xs">
              <Link to="/providers/$id/claim" params={{ id: p.id }}>Claim listing</Link>
            </Button>
          )}
        </div>
      </div>
    </article>
  );
}
